import { Box, FormControlLabel, FormGroup, SelectChangeEvent } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BriaCheckbox from "../../../../components/common/BriaCheckbox/BriaCheckbox";
import BriaDropDown from "../../../../components/common/BriaDropDown/BriaDropDown.tsx";
import BriaUpgradeOverlay from "../../../../components/common/BriaUpgradeOverlay/BriaUpgradeOverlay.tsx";
import InputLayout from "../../../../layout/InputLayout/InputLayout.tsx";
import { OrgFeatures } from "../../../../models/billing.ts";
import { ImageEditingViewMode } from "../../../../models/image-to-image.ts";
import { AutomotiveConfig, ImageEditingIframeConfig } from "../../../../models/new-iframe.ts";
import iframeStore from "../../iframe-store";
import styles from "../NewIframeEditor.module.scss";

const IframeAutomotiveConfig = () => {
	const { t } = useTranslation("translation", { keyPrefix: "newIframes.editor.form.automotiveConfig" });
	const { iframeForm, handleFormChange } = iframeStore;

	const handleOptionChange = (checked: boolean, option: string) => {
		handleFormChange("config", {
			...iframeForm.config,
			automotive_config: {
				...iframeForm.config.automotive_config,
				[option]: checked,
			} as AutomotiveConfig,
		});
	};

	const handleEnabledBulkChange = (checked: boolean) => {
		const existingConfig = iframeForm.config.automotive_config;
		const updatedConfig: Partial<ImageEditingIframeConfig> = {
			...existingConfig,
			enable_bulk: checked,
		};

		const isEnableBulkChecked = updatedConfig.enable_bulk ?? false;
		const newDefaultViewMode: ImageEditingViewMode = isEnableBulkChecked
			? existingConfig?.default_view_mode ?? ImageEditingViewMode.SINGLE
			: ImageEditingViewMode.SINGLE;

		handleFormChange("config", {
			...iframeForm.config,
			automotive_config: {
				...updatedConfig,
				default_view_mode: newDefaultViewMode,
			} as AutomotiveConfig,
		});
	};

	const handleDefaultViewModeChange = (e: SelectChangeEvent<ImageEditingViewMode>) => {
		handleFormChange("config", {
			...iframeForm.config,
			automotive_config: {
				...(iframeForm.config.automotive_config ?? {}),
				default_view_mode: e.target.value as ImageEditingViewMode,
			} as AutomotiveConfig,
		});
	};

	return (
		<BriaUpgradeOverlay feature={OrgFeatures.AUTOMOTIVE}>
			<Box>
				<FormGroup>
					<FormControlLabel
						control={
							<BriaCheckbox
								checked={iframeForm.config.automotive_config?.enable_project_management ?? false}
								onChange={(e) => handleOptionChange(e.target.checked, "enable_project_management")}
							/>
						}
						label={t("projectManager.label")}
					/>
				</FormGroup>

				<InputLayout label={t("availableTabs.label")} className={styles.defaultResetBehavior}>
					<FormGroup>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm.config.automotive_config?.image_size ?? false}
									onChange={(e) => handleOptionChange(e.target.checked, "image_size")}
								/>
							}
							label={t("availableTabs.imageSize")}
						/>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm.config.automotive_config?.scenery ?? false}
									onChange={(e) => handleOptionChange(e.target.checked, "scenery")}
								/>
							}
							label={t("availableTabs.scenery")}
						/>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm.config.automotive_config?.reflections ?? false}
									onChange={(e) => handleOptionChange(e.target.checked, "reflections")}
								/>
							}
							label={t("availableTabs.reflections")}
						/>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm.config.automotive_config?.effects ?? false}
									onChange={(e) => handleOptionChange(e.target.checked, "effects")}
								/>
							}
							label={t("availableTabs.effects")}
						/>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm.config.automotive_config?.harmonization ?? false}
									onChange={(e) => handleOptionChange(e.target.checked, "harmonization")}
								/>
							}
							label={t("availableTabs.harmonization")}
						/>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm.config.automotive_config?.modify_objects ?? false}
									onChange={(e) => handleOptionChange(e.target.checked, "modify_objects")}
								/>
							}
							label={t("availableTabs.modifyObjects")}
						/>
					</FormGroup>
				</InputLayout>

				<FormGroup>
					<Box className={styles.checkboxContainer}>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={iframeForm?.config.automotive_config?.enable_bulk ?? false}
									onChange={(e) => handleEnabledBulkChange(e.target.checked)}
								/>
							}
							label={t("bulkMode.label")}
						/>
					</Box>
				</FormGroup>
				{iframeForm?.config.automotive_config?.enable_bulk && (
					<InputLayout label={t("defaultViewMode.label")} className={styles.defaultResetBehavior}>
						<BriaDropDown
							value={iframeForm?.config.automotive_config?.default_view_mode}
							onChange={handleDefaultViewModeChange}
							placeholder={t("defaultViewMode.placeholder")}
							items={Object.values(ImageEditingViewMode).map((mode) => ({
								key: t(mode),
								value: mode,
							}))}
						/>
					</InputLayout>
				)}
			</Box>
		</BriaUpgradeOverlay>
	);
};

export default observer(IframeAutomotiveConfig);
